/* Create / Edit user — slide-over drawer with client-side validation and
   single-role assignment. Creating a user sends an email INVITE (the invitee
   sets their own password via a link); the admin never types a password. A user
   has exactly one role (the backend stores a single role_id); picker is
   single-select. */

import React, { useEffect, useState } from "react";
import s from "./userManagement.module.css";
import { IoClose } from "react-icons/io5";
import { createUser, updateUser } from "../../rbac/rbacService";
import { showToast } from "../../utils/Toastify/Toastify";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Only the two states an admin actually toggles. "Invited" is a system state
// (set on invite, cleared on acceptance) and is never chosen manually.
const STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const emptyForm = {
  name: "",
  email: "",
  title: "",
  phone: "",
  status: "invited",
  roleIds: [],
};

const UserFormModal = ({ open, onClose, editingUser, roles = [], onSaved }) => {
  const isEdit = Boolean(editingUser);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Editing an Admin: their EMAIL is locked (prevents lockout from a typo). Their
  // ROLE can be changed (e.g. to undo an accidental admin); the backend still
  // blocks demoting the LAST admin and surfaces a clear error if you try.
  const editingAdmin =
    isEdit &&
    (editingUser.roleIds || []).some((rid) => {
      const role = roles.find((r) => r.id === rid);
      return role && role.system && role.name === "Admin";
    });

  // Only STAFF-assignable roles are offered: Admin + every custom role. The
  // Creator and Subscriber system roles are audience/content account-types, not
  // back-office staff roles, so they're never assignable here.
  const assignableRoles = roles.filter(
    (r) => !(r.system && (r.name === "Creator" || r.name === "Subscriber"))
  );

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (editingUser) {
      setForm({
        name: editingUser.name || "",
        email: editingUser.email || "",
        title: editingUser.title || "",
        phone: editingUser.phone || "",
        status: editingUser.status || "active",
        // Single role: keep only the first assigned role if legacy data has more.
        roleIds: (editingUser.roleIds || []).slice(0, 1),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, editingUser]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  // Single-role: selecting a role replaces any previous choice.
  const selectRole = (roleId) => {
    setForm((f) => ({ ...f, roleIds: [roleId] }));
    setErrors((e) => ({ ...e, roleIds: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email";
    if (form.roleIds.length === 0) e.roleIds = "Select a role";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        title: form.title.trim(),
        phone: form.phone.trim(),
        status: form.status,
        roleIds: form.roleIds,
      };
      if (isEdit) {
        await updateUser(editingUser.id, payload);
        showToast("User updated", "success");
      } else {
        const created = await createUser(payload);
        // The account is always created; the email may or may not have gone out.
        if (created?.emailSent === false) {
          showToast(
            "User created, but the invite email couldn't be sent. Use “Resend invite” once email is working.",
            "warning"
          );
        } else {
          showToast(`Invitation sent to ${payload.email}`, "success");
        }
      }
      onSaved?.();
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to save user", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={s.modalOverlay} onClick={onClose}>
      <div
        className={s.drawer}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={s.drawerHeader}>
          <div>
            <h3 className={s.drawerTitle}>
              {isEdit ? "Edit user" : "Invite user"}
            </h3>
            <p className={s.drawerSub}>
              {isEdit
                ? "Update account details and role"
                : "They'll get an email to set their password and activate the account"}
            </p>
          </div>
          <button className={s.iconBtn} onClick={onClose} aria-label="Close">
            <IoClose />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={s.drawerBody} id="userForm">
          <div className={s.fieldRow}>
            <div className={s.field}>
              <label className={s.label}>
                Full name <span className={s.required}>*</span>
              </label>
              <input
                className={`${s.input} ${errors.name ? s.inputError : ""}`}
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Jane Cooper"
                maxLength={60}
              />
              {errors.name && <span className={s.errorText}>{errors.name}</span>}
            </div>
            <div className={s.field}>
              <label className={s.label}>Job title</label>
              <input
                className={s.input}
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="Content Editor"
                maxLength={60}
              />
            </div>
          </div>

          <div className={s.fieldRow}>
            <div className={s.field}>
              <label className={s.label}>
                Email <span className={s.required}>*</span>
              </label>
              <input
                className={`${s.input} ${errors.email ? s.inputError : ""}`}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="jane@company.com"
                type="email"
                disabled={isEdit}
              />
              {errors.email && (
                <span className={s.errorText}>{errors.email}</span>
              )}
            </div>
            <div className={s.field}>
              <label className={s.label}>Phone</label>
              <input
                className={s.input}
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                placeholder="+1 555 0100"
              />
            </div>
          </div>

          {/* Status only applies when EDITING. On invite, the account is always
              created as "Invited" until the person accepts, so the control is
              hidden to avoid a misleading no-op choice. */}
          {isEdit && (
            <div className={s.field}>
              <label className={s.label}>Account status</label>
              <select
                className={s.select}
                value={form.status}
                disabled={editingAdmin}
                onChange={(e) => setField("status", e.target.value)}
              >
                {STATUSES.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={s.field}>
            <label className={s.label}>
              Role <span className={s.required}>*</span>
            </label>
            <span className={s.hint}>
              The user receives the permissions of their assigned role.
            </span>
            <div className={s.roleSelectList}>
              {assignableRoles.map((role) => {
                const active = form.roleIds[0] === role.id;
                return (
                  <label
                    key={role.id}
                    className={`${s.roleOption} ${active ? s.roleOptionActive : ""}`}
                  >
                    <input
                      type="radio"
                      name="role"
                      className={s.checkbox}
                      checked={active}
                      onChange={() => selectRole(role.id)}
                    />
                    <span className={s.roleOptionMeta}>
                      <span className={s.roleOptionName}>
                        <span
                          className={s.roleChipDot}
                          style={{ background: role.color }}
                        />
                        {role.name}
                      </span>
                      <span className={s.roleOptionDesc}>
                        {role.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.roleIds && (
              <span className={s.errorText}>{errors.roleIds}</span>
            )}
          </div>
        </form>

        <div className={s.drawerFooter}>
          <button className={s.btnGhost} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className={s.btnPrimary}
            type="submit"
            form="userForm"
            disabled={saving}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;
